import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

// relatorio-microservice, AcessosController. Lê o login_log do auth-server (mesmo
// schema): uma linha por login bem-sucedido. Refresh de token não conta, então
// "acesso" aqui é login. Só usuários de cliente (ADMINISTRADOR/OPERADOR).
export const ACESSOS_PATH = "relatorio/Acessos";

// O ObjectMapper do relatorio usa NON_NULL: campo nulo não vem no JSON, por isso
// os opcionais abaixo.

export type StatusInstituicao = "OK" | "ATENCAO" | "INATIVA" | "NUNCA";
export type StatusUsuario = "OK" | "ATENCAO" | "INATIVO" | "NUNCA";
/** W = web, M = aplicativo, X = as duas. */
export type Origem = "W" | "M" | "X";

export interface Periodo {
  /** yyyy-MM-dd, inclusivo. */
  inicio: string;
  fim: string;
}

export interface ConfiguracaoAcessos {
  diasAtencaoInstituicao: number;
  diasInativaInstituicao: number;
  diasAtencaoUsuario: number;
  diasInativoUsuario: number;
}

export interface ResumoAcessos {
  instituicoes: number;
  instituicoesComAcessoHoje: number;
  instituicoesComAcessoPeriodo: number;
  instituicoesOk: number;
  instituicoesAtencao: number;
  instituicoesInativas: number;
  instituicoesNunca: number;
  instituicoesReativadas: number;
  usuarios: number;
  usuariosAcessaramPeriodo: number;
  usuariosNovos: number;
  usuariosReativados: number;
  usuariosInativos: number;
  usuariosNunca: number;
  logins: number;
  loginsPeriodoAnterior: number;
  loginsMobile: number;
  inicioHistorico?: string;
}

export interface AcessoInstituicao {
  id: number;
  codigo?: string;
  nomeFantasia?: string;
  razaoSocial?: string;
  cnpj?: string;
  situacaoAdmin?: string;
  primeiroAcesso?: string;
  primeiroAcessoUsuario?: string;
  primeiroAcessoAntesDoLog?: boolean;
  ultimoAcesso?: string;
  ultimoAcessoUsuario?: string;
  diasSemAcesso?: number;
  status: StatusInstituicao;
  usuariosAtivos: number;
  usuariosAcessaram: number;
  cobertura?: number;
  logins: number;
  loginsPeriodoAnterior: number;
  diasAtivos: number;
  loginsWeb: number;
  loginsMobile: number;
  concentracao?: number;
  dependeUmUsuario?: boolean;
  reativada?: boolean;
}

export interface FiltroAcessoInstituicao extends Partial<Periodo> {
  busca?: string;
  situacoes?: string[];
  status?: StatusInstituicao[];
  dependeUmUsuario?: boolean;
  reativada?: boolean;
}

export interface AcessoUsuario {
  id: number;
  nome?: string;
  login?: string;
  tipoUsuario?: string;
  situacao?: string;
  idInstituicao?: number;
  nomeInstituicao?: string;
  situacaoInstituicao?: string;
  cadastro?: string;
  primeiroAcesso?: string;
  primeiroAcessoAntesDoLog?: boolean;
  ultimoAcesso?: string;
  diasSemAcesso?: number;
  status: StatusUsuario;
  logins: number;
  diasAtivos: number;
  loginsWeb: number;
  loginsMobile: number;
  origemPredominante?: Origem;
  reativado?: boolean;
}

export interface FiltroAcessoUsuario extends Partial<Periodo> {
  busca?: string;
  idInstituicao?: number;
  situacoesInstituicao?: string[];
  situacaoUsuario?: string;
  status?: StatusUsuario[];
  origem?: Origem;
  loginsMin?: number;
  loginsMax?: number;
  primeiroAcessoDe?: string;
  primeiroAcessoAte?: string;
  ultimoAcessoDe?: string;
  ultimoAcessoAte?: string;
  cadastradoAte?: string;
  reativado?: boolean;
}

export interface PontoEngajamento {
  dia: string;
  dau: number;
  wau: number;
  mau: number;
  stickiness?: number;
}

export interface CelulaMapaCalor {
  /** 1 = domingo ... 7 = sábado. */
  diaSemana: number;
  hora: number;
  logins: number;
}

export interface Engajamento {
  serie: PontoEngajamento[];
  mapaCalor: CelulaMapaCalor[];
  logins: number;
  loginsWeb: number;
  loginsMobile: number;
  loginsForaHorario: number;
}

export interface Trial {
  id: number;
  nomeFantasia?: string;
  situacaoAdmin?: string;
  inicioTrial?: string;
  fimTrial?: string;
  primeiroAcesso?: string;
  diasAtePrimeiroAcesso?: number;
  ultimoAcesso?: string;
  logins: number;
  usuariosAcessaram: number;
}

export interface TrialsAcesso {
  trials: number;
  acessaram: number;
  acessaramEm3Dias: number;
  acessaramEm7Dias: number;
  mediaDiasAtePrimeiroAcesso?: number;
  instituicoes: Trial[];
}

export interface Coorte {
  /** yyyy-MM. */
  mes: string;
  usuarios: number;
  /** [0] = mês do primeiro login; null = coorte vazia. */
  retencao: (number | null)[];
}

export interface ChurnAcessos {
  semanas: { semana: number; mediaLogins?: number }[];
  mediaSemanalAtivas?: number;
  medianaDiasSemAcessoNoCancelamento?: number;
  canceladas: {
    id: number;
    nomeFantasia?: string;
    dataTermino?: string;
    ultimoAcesso?: string;
    diasSemAcessoNoCancelamento?: number;
    logins12Semanas: number;
  }[];
}

const KEY = ["acessos"] as const;

export function useConfiguracaoAcessos() {
  return useQuery({
    queryKey: [...KEY, "configuracao"],
    queryFn: async () => (await api.get<ConfiguracaoAcessos>(`${ACESSOS_PATH}/Configuracao`)).data,
    staleTime: 5 * 60_000,
  });
}

export function useSalvarConfiguracaoAcessos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: ConfiguracaoAcessos) => (await api.put<ConfiguracaoAcessos>(`${ACESSOS_PATH}/Configuracao`, dto)).data,
    // Status, contagens de inativos e reativados dependem dos limites.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResumoAcessos(periodo: Periodo) {
  return useQuery({
    queryKey: [...KEY, "resumo", periodo],
    queryFn: async () => (await api.get<ResumoAcessos>(`${ACESSOS_PATH}/Resumo`, { params: periodo })).data,
  });
}

export function useAcessoInstituicao(id: number, periodo: Periodo) {
  return useQuery({
    queryKey: [...KEY, "instituicao", id, periodo],
    queryFn: async () => (await api.get<AcessoInstituicao>(`${ACESSOS_PATH}/Instituicao/${id}`, { params: periodo })).data,
    enabled: Number.isFinite(id),
  });
}

export function useEngajamento(periodo: Periodo, idInstituicao?: number) {
  return useQuery({
    queryKey: [...KEY, "engajamento", periodo, idInstituicao],
    queryFn: async () =>
      (await api.get<Engajamento>(`${ACESSOS_PATH}/Engajamento`, { params: { ...periodo, idInstituicao } })).data,
  });
}

export function useTrials(periodo: Periodo) {
  return useQuery({
    queryKey: [...KEY, "trials", periodo],
    queryFn: async () => (await api.get<TrialsAcesso>(`${ACESSOS_PATH}/Trials`, { params: periodo })).data,
  });
}

export function useCoortes(meses: number) {
  return useQuery({
    queryKey: [...KEY, "coortes", meses],
    queryFn: async () => (await api.get<Coorte[]>(`${ACESSOS_PATH}/Coortes`, { params: { meses } })).data,
  });
}

export function useChurn(meses: number) {
  return useQuery({
    queryKey: [...KEY, "churn", meses],
    queryFn: async () => (await api.get<ChurnAcessos>(`${ACESSOS_PATH}/Churn`, { params: { meses } })).data,
  });
}

export const INSTITUICOES_PATH = `${ACESSOS_PATH}/Instituicoes`;
export const USUARIOS_PATH = `${ACESSOS_PATH}/Usuarios`;
export const ACESSOS_KEY = KEY;
